import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  UpgradeDialog,
  Button,
  List,
  CheckIcon,
  Image,
  EmptyState,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useAccount } from '~publish/contexts/AccountContext'
import { getBillingUrl } from '~publish/helpers/navigateToBilling'

import styles from './UpgradePathForCreate.module.css'

const UpgradePathForCreate = (): JSX.Element => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  const { account } = useAccount()
  useEffect(() => {
    if (account) {
      const commonTrackingProperties =
        account?.currentOrganization?.commonTrackingProperties || {}
      BufferTracker.upgradePathViewed({
        organizationId: account?.currentOrganization?.id || '',
        product: 'publish',
        upgradePathName: 'ideas-legacyUpgrade',
        clientName: Client.PublishWeb,
        cta: 'ideasModalBtn-migration-cta',
        ...commonTrackingProperties,
      })
    }
  }, [account])

  return (
    <section>
      <EmptyState className={styles.emptyState}>
        <EmptyState.Illustration src="https://buffer-publish.s3.amazonaws.com/images/content-empty-ideas.svg" />
        <EmptyState.Heading>
          {t('content.upgradePath.empty.title')}
        </EmptyState.Heading>
        <EmptyState.Description>
          {t('content.upgradePath.empty.subtitle')}
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button
            variant="primary"
            onClick={(): void => {
              location.replace(getBillingUrl('ideasModalBtn-migration-cta'))
            }}
          >
            {t('content.upgradePath.empty.button')}
          </Button>
        </EmptyState.Actions>
      </EmptyState>
      <UpgradeDialog open={open} onOpenChange={setOpen}>
        <UpgradeDialog.Content size="large" className={styles.dialogContent}>
          <UpgradeDialog.TextContent>
            <UpgradeDialog.Title>
              {t('content.upgradePath.modal.title')}
            </UpgradeDialog.Title>
            <p>
              <Trans
                i18nKey="content.upgradePath.modal.description"
                components={[
                  <a
                    href="https://buffer.com/ideas"
                    target="_blank"
                    rel="noreferrer"
                    key="ideas-link"
                    className={styles.link}
                  >
                    Ideas
                  </a>,
                ]}
              />
            </p>
            <List className={styles.featureList}>
              <List.Item icon={CheckIcon}>
                {t('content.upgradePath.modal.bullet1')}
              </List.Item>
              <List.Item icon={CheckIcon}>
                {t('content.upgradePath.modal.bullet2')}
              </List.Item>
              <List.Item icon={CheckIcon}>
                {t('content.upgradePath.modal.bullet3')}
              </List.Item>
              <List.Item icon={CheckIcon}>
                {t('content.upgradePath.modal.bullet4')}
              </List.Item>
              <List.Item icon={CheckIcon}>
                {t('content.upgradePath.modal.bullet5')}
              </List.Item>
              <List.Item icon={CheckIcon}>
                {t('content.upgradePath.modal.bullet6')}
              </List.Item>
            </List>
            <UpgradeDialog.Actions>
              <Button
                variant="primary"
                size="large"
                as="a"
                href={getBillingUrl('ideasModalBtn-migration-cta')}
                target="_blank"
                rel="noreferrer"
              >
                {t('content.upgradePath.modal.button')}
              </Button>
            </UpgradeDialog.Actions>
          </UpgradeDialog.TextContent>
          <UpgradeDialog.Image>
            <Image
              src="https://buffer-publish.s3.amazonaws.com/images/content-upgrade-path.png"
              alt="Upgrade plan"
              className={styles.upgradeImage}
            />
          </UpgradeDialog.Image>
        </UpgradeDialog.Content>
      </UpgradeDialog>
    </section>
  )
}

export default UpgradePathForCreate
